import { AgGridColumn } from 'ag-grid-react';
import { pipe } from 'ramda';
import React, { FC, useMemo } from 'react';
import { UserStatus } from 'api/models';
import { PartnerCompany, PartnerUser } from 'api/partner/types';
import { withExternalFilter } from 'components/atoms/grid';
import { TeamMembersTabsEnum } from 'components/organisms/module-settings/team-members/types';
import { useTranslate } from 'hooks/use-translation';
import { useAvailableLicensesQuery } from 'queries/billing';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { Grid, GridContainer } from '../component';
import {
  PartnerTeamMemberListEmptyState,
  PartnerTeamMemberNoSearchResultState,
} from './empty-state';
import {
  NameRenderer,
  RoleRenderer,
  MoreActionsRenderer,
  PermissionRenderer,
  AvatarRenderer,
} from './table-cells';

interface Props {
  partner: PartnerCompany;
  users: PartnerUser[];
  currentTab: TeamMembersTabsEnum;
  filterFunction: (record) => boolean;
  onEdit: (user: PartnerUser) => void;
  onReactivate: (user: PartnerUser) => void;
  onDeactivate: (user: PartnerUser) => void;
  onResendInvite: (user: PartnerUser) => void;
}
export const AgGrid = pipe(pipe(() => Grid, withExternalFilter))();
const onModelUpdated = ({ api }) => {
  if (api.getDisplayedRowCount() === 0) {
    api.showNoRowsOverlay();
  } else {
    api.hideOverlay();
  }
};
const renderNoOverlay = ({ tab, api, data }) => {
  if (api.getDisplayedRowCount() === 0 && data.length > 0) {
    return <PartnerTeamMemberNoSearchResultState />;
  }

  return <PartnerTeamMemberListEmptyState viewType={tab} />;
};

export const TeamMembersGrid: FC<Props> = ({
  users,
  filterFunction,
  onEdit,
  onReactivate,
  onDeactivate,
  onResendInvite,
  currentTab: currentViewType,
  partner,
}) => {
  const translate = useTranslate();
  const availableLicenses = useAvailableLicensesQuery();
  const noOverlayParams = useMemo(
    () => ({ tab: currentViewType, data: users }),
    [currentViewType, users],
  );

  return (
    <GridContainer className="ag-theme-balham">
      <AgGrid
        suppressContextMenu
        suppressRowClickSelection
        suppressCellSelection
        defaultColDef={{
          filter: false,
          sortable: true,
          resizable: false,
          wrapText: true,
          suppressMovable: true,
          suppressMenu: true,
          editable: false,
          rowDrag: false,
          unSortIcon: true,
        }}
        size="large"
        getRowClass={({ data }) =>
          data.userStatus === UserStatus.Inactive ? 'in-active' : ''
        }
        rowData={users}
        frameworkComponents={{
          NameRenderer,
          RoleRenderer,
          PermissionRenderer,
          MoreActionsRenderer,
          AvatarRenderer,
        }}
        filter={filterFunction}
        onModelUpdated={onModelUpdated}
        noRowsOverlayComponentParams={noOverlayParams}
        noRowsOverlayComponentFramework={renderNoOverlay}
      >
        <AgGridColumn
          width={50}
          headerName=""
          cellRenderer="AvatarRenderer"
          field="photoUrl"
          sortable={false}
        />
        <AgGridColumn
          flex={1}
          field="userId"
          editable={false}
          headerName={translate('name')}
          cellRenderer="NameRenderer"
          sortable
          comparator={(valueA, valueB, { data: recordA }, { data: recordB }) =>
            sortByText(fullName(recordA), fullName(recordB))
          }
        />
        <AgGridColumn
          flex={1}
          field="emailId"
          editable={false}
          headerName={translate('email')}
          sortable
        />
        <AgGridColumn
          flex={1}
          field="role"
          editable={false}
          headerName={translate('role')}
          cellRenderer="RoleRenderer"
          sortable
          comparator={(valueA, valueB) =>
            sortByText(valueA?.roleName ?? '', valueB?.roleName ?? '')
          }
        />
        <AgGridColumn
          flex={1}
          field="permissionSetName"
          editable={false}
          headerName={translate('permission')}
          sortable
          cellRenderer="PermissionRenderer"
        />
        <AgGridColumn
          width={48}
          editable={false}
          sortable={false}
          cellRenderer="MoreActionsRenderer"
          cellRendererParams={{
            onEdit,
            onDeactivate,
            onReactivate,
            onResendInvite,
            partnerAdmins: partner.admins,
            availableLicensesInAccount: availableLicenses,
            availableLicensesForPartner: partner.maxSeat - partner.usedSeat,
            partnerActiveStatus: partner.active,
          }}
        />
      </AgGrid>
    </GridContainer>
  );
};
